import { FC, ReactElement } from 'react'

import {
  Text,
  GreyGrey80,
  Primary13Regular,
  useNotifications,
  SimpleModal
} from '@sequencehq/core-components'

import { InvoiceUpdateReviewOutcome } from '../../types'

/**
 * Confirmation dialog for applying the agent's proposed invoice changes. The
 * caller passes the fully-formed CONFIRMED outcome (the edited fields), so this
 * component only owns the copy and the submit/notify flow. Dismissal is a
 * separate flow (see `useDismissInvoiceUpdateReviewTask`) and never routes here.
 */
export const ModalInvoiceUpdateReviewResponse: FC<{
  trigger: ReactElement
  outcome: Extract<InvoiceUpdateReviewOutcome, { outcome: 'CONFIRMED' }>
  invoiceLabel: string
  completeTask: (outcome: InvoiceUpdateReviewOutcome) => Promise<boolean>
}> = ({ trigger, outcome, invoiceLabel, completeTask }) => {
  const { displayNotification } = useNotifications()

  return (
    <SimpleModal
      variant="dialog"
      trigger={trigger}
      title="Update invoice"
      onSubmit={async () => {
        const succeeded = await completeTask(outcome)

        if (!succeeded) {
          displayNotification('Something went wrong. Please try again.', {
            type: 'error'
          })
          return false
        }

        displayNotification(`${invoiceLabel} updated`, { type: 'success' })

        return true
      }}
      submitButtonText="Update invoice"
      submittingButtonText="Updating invoice..."
      dialogPosition="top"
    >
      <Text {...Primary13Regular} color={GreyGrey80}>
        {`Apply the proposed changes to ${invoiceLabel}? The invoice will be updated.`}
      </Text>
    </SimpleModal>
  )
}
